import CommonMapper from '@/modules/core/mappers/CommonMapper'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import TravelHiringAnswers from '@/modules/travel/hiring/models/APP/TravelHiringAnswers'
import type TravelGetRecoveryResponse from '@/modules/travel/hiring/models/MID/responses/TravelGetRecoveryResponse'
import type TravelInsurance from '@/modules/travel/common/models/APP/TravelInsurance'
import Insured from '@/modules/common/common/models/APP/Insured'
import { UserRoleEnum } from '@/modules/core/models/enums'
import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import { EXTENSION_MIMETYPE } from '@/modules/common/common/constants/CommonConstants'

function mapToInsured(data: any, role: UserRoleEnum, file?: any) {
  const mimeType = file?.mimeType
    ? EXTENSION_MIMETYPE[file.mimeType.toLowerCase()]
    : 'application/pdf'

  const dataProtection = data.dataProtection

  const insured: Partial<Insured> = {
    riskNumber: data.riskNumber || 0,
    role: role,
    documentDetails: new DocumentDetails({
      personType: data.personType,
      documentType: data.documentType,
      documentNumber: data.documentNumber,
      documentExpirationDate: data.documentExpirationDate
        ? CommonMapper.fromMiddlewareDate(data.documentExpirationDate)
        : undefined,
      documentId: file?.documentId || file?.documentData,
      fileName: file?.fileName,
      mimeType: mimeType
    }),
    addressDetails: new AddressDetails({
      postalCode: data.postalCode,
      province: data.province,
      city: data.city,
      address: data.street,
      country: data.nationality,
      houseType: data.houseType ? String(data.houseType) : undefined,
      gateway: data.gateway,
      stair: data.stair,
      floor: data.floor,
      doorway: data.doorway,
      addressExtra: data.restAddress,
      addressNormalize: data.addressNormalice,
      streetType: data.streetType,
      roadNumber: data.house
    }),
    userDetails: new UserDetails({
      birthDate: data.birthDate ? CommonMapper.fromMiddlewareDate(data.birthDate) : undefined,
      name: data.name?.toUpperCase(),
      surname1: data.firstSurname?.toUpperCase(),
      surname2: data.secondSurname?.toUpperCase(),
      gender: data.sex, // H / M
      email: data.email,
      phone: data.mobilePhone ? String(data.mobilePhone) : undefined,
      prefix: data.prefix
    }),
    privacyPolicyConsent: dataProtection?.privacyPolicyConsent,
    asisaNewsConsent: dataProtection?.asisaNewsConsent,
    thirdPartyWrittenAdvertising: dataProtection?.thirdPartyTelephoneAdvertising,
    thirdPartyTelephoneAdvertising: dataProtection?.thirdPartyWrittenAdvertising,
    dataTransfer: dataProtection?.dataTransfer
  }

  if (!['undefined', 'null'].includes(typeof data.isFirstInsured)) {
    insured.isFirstInsured = data.isFirstInsured
  }

  if (!['undefined', 'null'].includes(typeof data.sendPrecontractualDocuments)) {
    insured.preContractualDocumentChecked = data.sendPrecontractualDocuments
  }

  return new Insured(insured)
}

export default class {
  static fromResponse(
    data: TravelGetRecoveryResponse,
    product: TravelInsurance
  ): TravelHiringAnswers {
    const file = data.documents?.[0]
    const holder = mapToInsured(data.policyHolder, data.policyHolder.role, file)
    let isFirstOne = true
    const insureds =
      data.insureds.map((insured: any, index: number) => {
        const isHolder =
          data.policyHolder.role === UserRoleEnum.HOLDER_INSURED && insured.riskNumber === 1
        const document = data.documents[index]
        const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
        const isFirstInsured = !isHolder && isFirstOne
        if (isFirstInsured) {
          isFirstOne = false
        }

        return mapToInsured({ ...insured, isFirstInsured }, role, document)
      }) ?? []
    if (holder.role === UserRoleEnum.HOLDER_INSURED) {
      const preContractualDocumentChecked = holder.preContractualDocumentChecked
      const thirdPartiesAnswered = holder.thirdPartiesAnswered
      const thirdPartyTelephoneAdvertising = holder.thirdPartyTelephoneAdvertising
      const thirdPartyWrittenAdvertising = holder.thirdPartyWrittenAdvertising
      const dataTransfer = holder.dataTransfer
      const privacyPolicyConsent = holder.privacyPolicyConsent

      insureds[0].updateAttributes({
        preContractualDocumentChecked,
        thirdPartiesAnswered,
        thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising,
        dataTransfer,
        privacyPolicyConsent
      })
      insureds[0].userDetails.updateAttributes({
        email: holder.userDetails.email
      })
      insureds[0].healthPolicyNumber = data.insureds[0].healthPolicyNumber
      holder.updateAttributes(insureds[0])
    }
    return new TravelHiringAnswers({
      quotationId: data.quotationId,
      businessLine: data.hiring.businessLine,
      billingCycle: CommonMapper.fromDXPProductPrice(data.hiring.billingCycle),
      price: data.hiring.price,
      totalPrice: data.hiring.totalPrice,
      insuranceType: data.hiring.insuranceTypes?.[0],
      product: new Insurance({
        ...product,
        quotationNumber: String(data.hiring.quotationNumber),
        clientCode: String(data.hiring.clientCode),
        productCode: String(data.hiring.productCode)
      }),
      processStatus: new ProcessStatus({
        statusCode: data.hiring.statusCode,
        stepCode: data.hiring.stepCode
      }),
      startDate: data.hiring.insuranceStartDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.insuranceStartDate)
        : undefined,
      departureDate: data.hiring.departureDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.departureDate)
        : undefined,
      returnDate: data.hiring.returnDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.returnDate)
        : undefined,
      destinationType: data.hiring.destinationType,
      cancellationCoverage: data.hiring.allowCancellation,
      insureds,
      holder
    })
  }

  static toRequest() {}
}
